import { Component, forwardRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';
import { parsePhoneNumberWithError, AsYouType, CountryCode, getCountries, getCountryCallingCode } from 'libphonenumber-js';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TranslateModule } from '@ngx-translate/core';
import { User } from '../../pages/user/models/user.type';
import { UserService } from '../../pages/user/services/user.service';
interface CountryOption {
  code: string;
  dialCode: string;
  name: string;
  flag: string;
  searchText?: string;
}

@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    SelectModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    TranslateModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
  ],
})
export class PhoneInputComponent implements OnInit {
  countryControl = new FormControl<CountryOption | null | undefined>(null);
  phoneControl = new FormControl('');
  isValid = false;
  showError = true;
  user: User;

  countries: CountryOption[];
  constructor(private readonly userService: UserService) {}

  ngOnInit() {
    this.user = this.userService.getUser();
    this.countries = getCountries()
    .map((code) => {
      const name =
        new Intl.DisplayNames([this.user.defaultLanguage.languageCode.toLowerCase()], { type: 'region' }).of(code) || code;
      const dialCode = getCountryCallingCode(code);
      return {
        code,
        name,
        dialCode,
        flag: code.toLowerCase(),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, this.user.defaultLanguage.languageCode.toLowerCase()));
    const defaultCountry = this.countries.find((c) => c.code === this.user.defaultLanguage?.languageCode);
    this.countryControl.setValue(defaultCountry);
    this.countryControl.valueChanges.subscribe(() => {
      this.validatePhoneNumber();
    });
  }

  formatPhoneNumber(event: Event) {
    const input = event.target as HTMLInputElement;
    const country = this.countryControl.value;
    if (country) {
      const formatter = new AsYouType(country.code as CountryCode);
      const formatted = formatter.input(input.value);
      this.phoneControl.setValue(formatted, { emitEvent: false });
      this.validatePhoneNumber();
    }
  }

  validatePhoneNumber() {
    const value = this.phoneControl.value;
    const country = this.countryControl.value;

    if (!value || !country) {
      this.isValid = false;
      this.onChange(null);
      return;
    }

    try {
      const phoneNumber = parsePhoneNumberWithError(value, country.code as CountryCode);
      this.isValid = phoneNumber.isValid();
      this.onChange(this.isValid ? phoneNumber.format('E.164') : null);
    } catch (e) {
      console.error('Invalid phone number:', e);
      this.isValid = false;
      this.onChange(null);
    }

    this.onTouched();
  }

  private onChange: any = () => {};
  onTouched: any = () => {};

  writeValue(value: string): void {
    if (value) {
      try {
        const phoneNumber = parsePhoneNumberWithError(value);
        if (phoneNumber) {
          const country = this.countries.find(
            (c) => c.code === phoneNumber.country,
          );
          if (country) {
            this.countryControl.setValue(country);
            this.phoneControl.setValue(phoneNumber.formatNational());
            this.validatePhoneNumber();
          }
        }
      } catch (e) {
        console.error('Invalid phone number:', e);
      }
    } else {
      this.phoneControl.setValue('');
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}
